import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import ScalingSlider from '../components/recipes/ScalingSlider';
import { scaleIngredients, formatQuantity } from '../utils/ingredientScaler';
import { convertIngredients, convertStepTemperatures } from '../utils/unitConversion';
import { DIFFICULTY_LEVELS, SPICE_LEVELS, AGE_GROUPS } from '../constants/recipeConstants';
import {
  Clock, Users, Heart, Edit, Trash2,
  ChefHat, ShoppingCart, ExternalLink, StickyNote,
  Timer, Globe2, Flame, Star, ThumbsUp, Zap,
  Baby, ShieldAlert, AlertCircle, Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, EmptyState, Modal, TabBar, ChipSelector, LoadingSpinner } from '../components/common';
import { aiApi, recipeToAiPayload, adaptationToRecipe } from '../services/ai';

// Age bands mapped to a representative ageMonths for the adapt-baby endpoint (6–36).
const AGE_BAND_OPTIONS = [
  { value: 7, label: '6–9 months' },
  { value: 10, label: '9–12 months' },
  { value: 18, label: '12–24 months' },
  { value: 30, label: '2–3 years' },
];

const MetaChip = ({ icon: Icon, children, className = '' }) => (
  <span className={['inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 text-white text-xs', className].join(' ')}>
    <Icon size={14} aria-hidden="true" />
    <span>{children}</span>
  </span>
);

const ActionButton = ({ icon: Icon, label, active = false, success = false, danger = false, ...rest }) => (
  <button
    {...rest}
    className={[
      'flex flex-col items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors min-w-[64px]',
      active
        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
        : success
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : danger
        ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
        : 'text-gray-700 dark:text-night-text hover:bg-gray-100 dark:hover:bg-night-card',
    ].join(' ')}
  >
    <Icon size={18} aria-hidden="true" fill={active ? 'currentColor' : 'none'} />
    <span>{label}</span>
  </button>
);

export default function RecipeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    recipes, toggleFavorite, deleteRecipe, markAsCooked,
    addGroceryItemsFromRecipes, unitSystem, toggleUnitSystem, submitReview,
    addRecipe,
  } = useApp();
  const { canUseCookingMode, promptUpgrade, isPremium } = useSubscription();
  const recipe = recipes.find((r) => r.id === parseInt(id, 10));

  const [scaledServings, setScaledServings] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('ingredients');
  const [addedToGrocery, setAddedToGrocery] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [hasRated, setHasRated] = useState(false);

  // Baby/toddler adaptation (premium AI feature)
  const [showAdaptModal, setShowAdaptModal] = useState(false);
  const [adaptState, setAdaptState] = useState('pick'); // pick | loading | result | error
  const [ageMonths, setAgeMonths] = useState(null);
  const [adaptation, setAdaptation] = useState(null);
  const [savingAdaptation, setSavingAdaptation] = useState(false);

  useEffect(() => { if (recipe) setScaledServings(recipe.servings); }, [recipe]);

  if (!recipe) {
    return (
      <EmptyState
        title="Recipe not found"
        description="We couldn't find that recipe."
        action={<Button onClick={() => navigate('/browse')}>Back to Recipes</Button>}
      />
    );
  }

  let displayIngredients = scaledServings !== recipe.servings
    ? scaleIngredients(recipe.ingredients || [], recipe.servings, scaledServings)
    : recipe.ingredients || [];
  if (unitSystem === 'metric') displayIngredients = convertIngredients(displayIngredients, 'metric');

  const primaryImage = recipe.images?.find((img) => img.isPrimary) || recipe.images?.[0];

  const handleRate = async (rating) => {
    setUserRating(rating);
    setHasRated(true);
    await submitReview(recipe.id, rating);
  };

  const ingredientGroups = displayIngredients.reduce((groups, ing) => {
    const group = ing.group || 'Main';
    if (!groups[group]) groups[group] = [];
    groups[group].push(ing);
    return groups;
  }, {});

  const handleDelete = async () => {
    await deleteRecipe(recipe.id);
    navigate('/browse');
  };

  const handleAddToGrocery = async () => {
    await addGroceryItemsFromRecipes([recipe.id]);
    setAddedToGrocery(true);
    setTimeout(() => setAddedToGrocery(false), 2000);
  };

  const handleOpenAdapt = () => {
    if (!isPremium) {
      promptUpgrade('baby_adapt');
      return;
    }
    setAdaptState('pick');
    setAgeMonths(null);
    setAdaptation(null);
    setShowAdaptModal(true);
  };

  const handleAdapt = async () => {
    if (!ageMonths) return;
    setAdaptState('loading');
    try {
      const res = await aiApi.adaptForBaby(recipeToAiPayload(recipe), ageMonths);
      setAdaptation(res.adaptation);
      setAdaptState('result');
    } catch (err) {
      if (err?.code === 'premium_required') {
        setShowAdaptModal(false);
        promptUpgrade('baby_adapt');
        return;
      }
      setAdaptState('error');
    }
  };

  const handleSaveAdaptation = async () => {
    if (!adaptation || savingAdaptation) return;
    setSavingAdaptation(true);
    try {
      const newId = await addRecipe(adaptationToRecipe(adaptation, recipe));
      toast.success('Saved to your cookbook');
      setShowAdaptModal(false);
      navigate(`/recipes/${newId}`);
    } catch {
      toast.error('Could not save that recipe — please try again.');
    } finally {
      setSavingAdaptation(false);
    }
  };

  const tabs = [
    { value: 'ingredients', label: 'Ingredients' },
    { value: 'steps', label: 'Steps' },
    ...(recipe.nutrition ? [{ value: 'nutrition', label: 'Nutrition' }] : []),
    ...(recipe.notes ? [{ value: 'notes', label: 'Notes' }] : []),
  ];

  return (
    <div className="space-y-5">
      <div
        className="relative -mx-4 sm:mx-0 sm:rounded-2xl overflow-hidden h-64 sm:h-72 bg-cover bg-center"
        style={
          primaryImage
            ? { backgroundImage: `url(${recipe.images[activeImage]?.url || primaryImage.url})` }
            : recipe.image
              ? { backgroundImage: `url(${recipe.image})` }
              : undefined
        }
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-4 text-white">
          <h1 className="text-2xl font-bold drop-shadow">{recipe.title}</h1>
          {recipe.description && (
            <p className="text-sm text-white/85 mt-1 line-clamp-3">{recipe.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {recipe.totalTime > 0 && <MetaChip icon={Clock}>{recipe.totalTime} min</MetaChip>}
            {recipe.servings > 0 && <MetaChip icon={Users}>{recipe.servings} servings</MetaChip>}
            {recipe.cookCount > 0 && <MetaChip icon={ChefHat}>Cooked {recipe.cookCount}x</MetaChip>}
            {recipe.difficulty && (
              <MetaChip icon={Zap}>{DIFFICULTY_LEVELS[recipe.difficulty]?.label}</MetaChip>
            )}
            {recipe.spiceLevel > 0 && <MetaChip icon={Flame}>{SPICE_LEVELS[recipe.spiceLevel]?.icon}</MetaChip>}
          </div>
        </div>
      </div>

      {recipe.images?.length > 1 && (
        <div className="-mx-4 px-4 overflow-x-auto">
          <div className="flex gap-2 pb-1">
            {recipe.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                aria-label={img.caption || `Image ${i + 1}`}
                aria-pressed={activeImage === i}
                className={[
                  'flex-shrink-0 w-16 h-16 rounded-lg bg-cover bg-center border-2',
                  activeImage === i ? 'border-primary-500' : 'border-transparent',
                ].join(' ')}
                style={{ backgroundImage: `url(${img.url})` }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-night-muted">
        {(recipe.cuisine || recipe.country) && (
          <span className="inline-flex items-center gap-1">
            <Globe2 size={16} aria-hidden="true" />
            {[recipe.cuisine, recipe.country].filter(Boolean).join(' · ')}
          </span>
        )}
        {recipe.communityRating > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <Star size={16} fill="currentColor" aria-hidden="true" />
            <strong>{recipe.communityRating}</strong>
            <span className="text-gray-500 dark:text-night-muted">({recipe.communityRatingCount} reviews)</span>
          </span>
        )}
      </div>

      {recipe.ageGroups?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {recipe.ageGroups.map((ag) => (
            <span key={ag} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
              {AGE_GROUPS[ag]?.icon} {AGE_GROUPS[ag]?.label}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-5 gap-1 bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-2">
        <ActionButton icon={Heart} label={recipe.favorite ? 'Saved' : 'Save'} active={!!recipe.favorite} onClick={() => toggleFavorite(recipe.id)} />
        <ActionButton
          icon={ChefHat}
          label="Cook"
          onClick={() => {
            if (!canUseCookingMode()) { promptUpgrade('cooking_mode'); return; }
            navigate(`/recipes/${recipe.id}/cook`);
          }}
        />
        <ActionButton icon={ShoppingCart} label={addedToGrocery ? 'Added!' : 'Shop'} success={addedToGrocery} onClick={handleAddToGrocery} />
        <ActionButton icon={Edit} label="Edit" onClick={() => navigate(`/submit/${recipe.id}/edit`)} />
        <ActionButton icon={Trash2} label="Delete" danger onClick={() => setShowDeleteConfirm(true)} />
      </div>

      <div className="rounded-xl border border-primary-100 dark:border-primary-900/40 bg-primary-50 dark:bg-primary-900/20 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 flex items-center justify-center">
            <Baby size={20} aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-night-text">Adapt for baby or toddler</h3>
            <p className="text-sm text-gray-600 dark:text-night-muted mt-0.5">
              Get a safe, age-appropriate version of this recipe for your little one.
            </p>
          </div>
        </div>
        <Button className="mt-3 w-full" onClick={handleOpenAdapt}>
          <Baby size={18} aria-hidden="true" />
          Adapt this recipe{!isPremium && ' (Premium)'}
        </Button>
      </div>

      {recipe.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {recipe.tags.map((tag) => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-night-card text-gray-700 dark:text-night-text">
              {tag}
            </span>
          ))}
        </div>
      )}

      {recipe.sourceUrl && (
        <a
          href={recipe.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          <ExternalLink size={14} aria-hidden="true" />
          View Original Source
        </a>
      )}

      <div className="grid grid-cols-3 gap-2">
        {recipe.prepTime > 0 && (
          <div className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 dark:text-night-muted uppercase tracking-wide">Prep</div>
            <div className="text-base font-semibold text-gray-900 dark:text-night-text">{recipe.prepTime} min</div>
          </div>
        )}
        {recipe.cookTime > 0 && (
          <div className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 dark:text-night-muted uppercase tracking-wide">Cook</div>
            <div className="text-base font-semibold text-gray-900 dark:text-night-text">{recipe.cookTime} min</div>
          </div>
        )}
        {recipe.totalTime > 0 && (
          <div className="bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg p-3 text-center">
            <div className="text-xs text-primary-700 dark:text-primary-400 uppercase tracking-wide">Total</div>
            <div className="text-base font-semibold text-primary-700 dark:text-primary-300">{recipe.totalTime} min</div>
          </div>
        )}
      </div>

      <TabBar tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'ingredients' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <ScalingSlider servings={scaledServings || recipe.servings} onChange={setScaledServings} />
            <div role="group" aria-label="Unit system" className="inline-flex p-1 rounded-lg bg-gray-100 dark:bg-night-surface">
              {[
                { v: 'imperial', l: 'US' },
                { v: 'metric', l: 'Metric' },
              ].map((u) => {
                const sel = unitSystem === u.v;
                return (
                  <button
                    key={u.v}
                    aria-pressed={sel}
                    onClick={() => toggleUnitSystem(u.v)}
                    className={[
                      'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                      sel
                        ? 'bg-white dark:bg-night-card text-gray-900 dark:text-night-text shadow-sm'
                        : 'text-gray-600 dark:text-night-muted',
                    ].join(' ')}
                  >
                    {u.l}
                  </button>
                );
              })}
            </div>
          </div>

          {Object.entries(ingredientGroups).map(([group, items]) => (
            <div key={group}>
              {Object.keys(ingredientGroups).length > 1 && (
                <h4 className="text-sm font-semibold text-gray-900 dark:text-night-text mb-2">{group}</h4>
              )}
              <ul className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl divide-y divide-gray-100 dark:divide-night-card">
                {items.map((ing, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-2">
                    <span className="text-sm font-mono font-medium text-primary-700 dark:text-primary-400 min-w-[80px]">
                      {formatQuantity(ing.qty)} {ing.unit}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-night-text flex-1 text-right">{ing.item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'steps' && (
        <div className="space-y-3">
          {(recipe.steps || []).map((step, i) => {
            const instruction = unitSystem === 'metric' ? convertStepTemperatures(step.instruction, 'metric') : step.instruction;
            return (
              <div key={i} className="flex gap-3 bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-semibold flex items-center justify-center">
                  {step.order || i + 1}
                </div>
                <div className="flex-1 space-y-1.5">
                  <p className="text-sm text-gray-700 dark:text-night-text leading-relaxed">{instruction}</p>
                  {step.timerMinutes && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
                      <Timer size={14} aria-hidden="true" />
                      {step.timerMinutes} minutes
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          <Button onClick={() => markAsCooked(recipe.id)} className="w-full">
            <ChefHat size={18} aria-hidden="true" />
            Mark as Cooked
          </Button>
        </div>
      )}

      {activeTab === 'nutrition' && recipe.nutrition && (
        <div className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-night-text mb-3">Nutrition per Serving</h4>
          <div className="grid grid-cols-4 gap-3">
            {[
              { v: recipe.nutrition.calories, l: 'Calories', highlight: true },
              { v: `${recipe.nutrition.protein}g`, l: 'Protein' },
              { v: `${recipe.nutrition.carbs}g`, l: 'Carbs' },
              { v: `${recipe.nutrition.fat}g`, l: 'Fat' },
              { v: `${recipe.nutrition.fiber}g`, l: 'Fiber' },
              { v: `${recipe.nutrition.iron}mg`, l: 'Iron' },
              { v: `${recipe.nutrition.sodium}mg`, l: 'Sodium' },
              { v: `${recipe.nutrition.sugar}g`, l: 'Sugar' },
            ].map((n) => (
              <div key={n.l} className="text-center">
                <div className={[
                  'text-lg font-semibold',
                  n.highlight ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-night-text',
                ].join(' ')}>
                  {n.v}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-night-muted uppercase tracking-wide">{n.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'notes' && recipe.notes && (
        <div className="space-y-2">
          <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
            <StickyNote size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-gray-700 dark:text-night-text">{recipe.notes}</p>
          </div>
          {recipe.lastCookedAt && (
            <p className="text-xs text-gray-500 dark:text-night-muted">
              Last cooked: {new Date(recipe.lastCookedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-4 text-center">
        {!hasRated ? (
          <>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-night-text mb-2">Rate this Recipe</h4>
            <div className="inline-flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  aria-label={`Rate ${star} stars`}
                  onClick={() => handleRate(star)}
                  onMouseEnter={() => setUserRating(star)}
                  onMouseLeave={() => !hasRated && setUserRating(0)}
                  className={star <= userRating ? 'text-amber-500' : 'text-gray-300 dark:text-night-muted'}
                >
                  <Star size={28} fill={star <= userRating ? 'currentColor' : 'none'} aria-hidden="true" />
                </button>
              ))}
            </div>
          </>
        ) : (
          <span className="inline-flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400">
            <ThumbsUp size={18} aria-hidden="true" />
            Thanks for rating!
          </span>
        )}
      </div>

      <Modal isOpen={showAdaptModal} onClose={() => setShowAdaptModal(false)} title="Adapt for baby or toddler">
        {adaptState === 'pick' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-night-text">
              How old is your little one? We'll adjust textures, seasoning and portions to suit.
            </p>
            <ChipSelector
              label="Child's age"
              options={AGE_BAND_OPTIONS}
              value={ageMonths}
              onChange={setAgeMonths}
            />
            <Button onClick={handleAdapt} disabled={!ageMonths} className="w-full">
              <Baby size={18} aria-hidden="true" />
              Adapt recipe
            </Button>
          </div>
        )}

        {adaptState === 'loading' && (
          <div className="py-2 text-center">
            <LoadingSpinner />
            <p className="text-sm text-gray-600 dark:text-night-muted">Adapting this recipe for little ones…</p>
          </div>
        )}

        {adaptState === 'error' && (
          <div className="space-y-4">
            <p className="text-sm text-red-700 dark:text-red-400 flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
              We couldn't adapt this recipe just now — please try again.
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowAdaptModal(false)} className="flex-1">Close</Button>
              <Button onClick={() => setAdaptState('pick')} className="flex-1">Try Again</Button>
            </div>
          </div>
        )}

        {adaptState === 'result' && adaptation && (
          <div className="space-y-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-night-text">{adaptation.title}</h3>
              {typeof adaptation.suitableFromMonths === 'number' && (
                <span className="inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                  <Baby size={12} aria-hidden="true" />
                  Suitable from {adaptation.suitableFromMonths} months
                </span>
              )}
            </div>

            {adaptation.intro && (
              <p className="text-sm text-gray-700 dark:text-night-text">{adaptation.intro}</p>
            )}

            {adaptation.safetyNotes?.length > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
                <ShieldAlert size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Safety notes</h4>
                  <ul className="mt-1 space-y-1 text-sm text-gray-700 dark:text-night-text list-disc list-inside">
                    {adaptation.safetyNotes.map((note, i) => <li key={i}>{note}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {adaptation.ingredients?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-night-text mb-2">Ingredients</h4>
                <ul className="bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl divide-y divide-gray-100 dark:divide-night-card">
                  {adaptation.ingredients.map((ing, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-2">
                      <span className="text-sm font-mono font-medium text-primary-700 dark:text-primary-400 min-w-[80px]">
                        {[ing.quantity, ing.unit].filter(Boolean).join(' ')}
                      </span>
                      <span className="text-sm text-gray-700 dark:text-night-text flex-1 text-right">{ing.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {adaptation.steps?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-night-text mb-2">Steps</h4>
                <ol className="space-y-2">
                  {adaptation.steps.map((step, i) => (
                    <li key={i} className="flex gap-3 bg-white dark:bg-night-surface border border-gray-100 dark:border-night-card rounded-xl p-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-semibold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <p className="text-sm text-gray-700 dark:text-night-text leading-relaxed">{step}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button variant="secondary" onClick={() => setShowAdaptModal(false)}>Close</Button>
              <Button onClick={handleSaveAdaptation} disabled={savingAdaptation} className="flex-1">
                <Save size={18} aria-hidden="true" />
                {savingAdaptation ? 'Saving…' : 'Save to my cookbook'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete recipe?">
        <p className="text-sm text-gray-700 dark:text-night-text mb-4">
          Are you sure you want to delete "{recipe.title}"? This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
